import { FC, useCallback, useEffect, useState } from 'react';
import { Maps, Svg } from '@hopskipdrive/react-components';

import { PhotoOrInitials } from '../../../../../shared/components/PhotoOrInitials';
import { colorTokens } from '../../../../../shared/components/variables';
import { Rider } from '../../../../types/rideOverview';

import { DriverMarker, DriverStartMarker } from './CareDriverRoute.styles';
import CarIcon from './CarIcon';

interface CareDriverRouteProps {
  rideActualPolyline?: string;
  liveCoordinates: { lat: number; lng: number }[];
  shouldHideCarMarker?: boolean;
  ridersInDriverCar?: Rider[];
}

const staticEmptyMarkerSvg =
  '<svg xmlns="http://www.w3.org/2000/svg" width="52" height="52" viewBox="0 0 52 52" fill="none"></svg>';

export const CareDriverRoute: FC<CareDriverRouteProps> = ({
  rideActualPolyline,
  liveCoordinates,
  shouldHideCarMarker,
  ridersInDriverCar,
}) => {
  const [coordinatesOnMap, setCoordinatesOnMap] = useState<[number, number][]>([]);
  const [polylineInstance, setPolylineInstance] = useState<google.maps.Polyline>();
  const [isTooltipOpen, setIsTooltipOpen] = useState<boolean>(false);
  const driverStartPosition = coordinatesOnMap[0];
  const currentDriverPosition = coordinatesOnMap[coordinatesOnMap.length - 1];
  const prevDriverPosition = coordinatesOnMap[coordinatesOnMap.length - 2];

  const getMarkerRotation = (lastDriverPosition, driverPosition) => {
    // this is needed for pointing to the right direction
    const iconOffSetDeg = 0;
    const headingDirection = window.google.maps.geometry.spherical.computeHeading(lastDriverPosition, driverPosition);

    return headingDirection + iconOffSetDeg;
  };

  const getPosition = useCallback((LatLng) => {
    return new google.maps.LatLng(LatLng[0], LatLng[1]);
  }, []);

  const getPath = useCallback(() => {
    // this means a Path already exists, so do an incremental push instead
    if (polylineInstance) {
      const path = polylineInstance.getPath();

      path.push(new google.maps.LatLng(currentDriverPosition[0], currentDriverPosition[1]));

      return path;
    }

    return coordinatesOnMap.map((coordinate) => {
      return new google.maps.LatLng(coordinate[0], coordinate[1]);
    });
  }, [coordinatesOnMap, currentDriverPosition, polylineInstance]);

  useEffect(() => {
    // add incoming coordinates on the map
    if (liveCoordinates && liveCoordinates?.length) {
      setCoordinatesOnMap((prevCoords) => prevCoords.concat([[liveCoordinates[0].lat, liveCoordinates[0].lng]]));
    }
  }, [liveCoordinates]);

  useEffect(() => {
    if (rideActualPolyline && !coordinatesOnMap.length) {
      const coordinates = window.google.maps.geometry.encoding.decodePath(rideActualPolyline);

      setCoordinatesOnMap(coordinates.map((coordinate) => [coordinate.lat(), coordinate.lng()]));
    }
  }, [coordinatesOnMap.length, rideActualPolyline]);

  if (!coordinatesOnMap.length) {
    return null;
  }

  return (
    <>
      {!shouldHideCarMarker ? (
        <>
          <Maps.Marker position={getPosition(driverStartPosition)}>
            <DriverStartMarker />
          </Maps.Marker>
          <Maps.Marker position={getPosition(currentDriverPosition)}>
            <DriverMarker
              data-testid="driver-marker"
              style={{
                transform: `rotate(${
                  coordinatesOnMap.length > 1
                    ? getMarkerRotation(getPosition(prevDriverPosition), getPosition(currentDriverPosition))
                    : 0
                }deg)`,
              }}
            >
              <CarIcon />
            </DriverMarker>
          </Maps.Marker>
          <Maps.GoogleMarker
            onMouseOver={() => setIsTooltipOpen(true)}
            onMouseOut={() => setIsTooltipOpen(false)}
            key={'riders-in-driver-car-infowindow'}
            icon={{
              url: 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(staticEmptyMarkerSvg),
            }}
            position={getPosition(currentDriverPosition)}
            zIndex={2000}
          >
            {isTooltipOpen && (
              <Maps.InfoWindow position={getPosition(currentDriverPosition)}>
                <div
                  style={{
                    display: 'flex',
                    gap: '2px',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Svg icon="sidebar_riders" color={colorTokens.primary.main} />
                  {ridersInDriverCar?.map((rider) => (
                    <PhotoOrInitials
                      key={rider.slug}
                      fullName={`${rider.firstName} ${rider.lastName}`}
                      pictureUrl={rider.profilePictureUrl || ''}
                    />
                  ))}
                </div>
              </Maps.InfoWindow>
            )}
          </Maps.GoogleMarker>
        </>
      ) : null}

      <Maps.Polyline
        onLoad={setPolylineInstance}
        data-testid="driver-polyline"
        options={{
          path: getPath(),
          geodesic: true,
          strokeColor: '#1A4A7A',
          strokeOpacity: 1.0,
          strokeWeight: 4,
          zIndex: 2,
        }}
      />
    </>
  );
};
